import { useState, useEffect } from 'react'
import { ListChecks, FlaskConical, Check, ArrowRight, ArrowLeft } from 'lucide-react'
import { useLab } from '../../../context/LabContext'
import { Spectrophotometer, Cuvette } from '../../lab'
import InfoBox from '../InfoBox'

const steps = [
  { id: 1, title: 'Set Wavelength', short: 'Confirm 400 nm setting' },
  { id: 2, title: 'Zero with Blank', short: 'Insert blank and zero' },
  { id: 3, title: 'Measure 2.0×10⁻⁵ M', short: 'Insert Std 1' },
  { id: 4, title: 'Measure 5.0×10⁻⁵ M', short: 'Insert Std 2' },
  { id: 5, title: 'Measure 1.0×10⁻⁴ M', short: 'Insert Std 3' },
  { id: 6, title: 'Measure 5.0×10⁻⁴ M', short: 'Insert Std 4' },
  { id: 7, title: 'Measure 7.5×10⁻⁴ M', short: 'Insert Std 5' },
  { id: 8, title: 'Measure Cola Samples (×3)', short: 'Insert 3 cola samples' }
]

// Expected absorbance values (Beer-Lambert: A = εbC, proportional to concentration)
// Molar absorptivity scaled so 7.5×10⁻⁴ M ≈ 0.750
const EXPECTED_ABSORBANCES: Record<string, number> = {
  blank: 0.000,
  std1: 0.020,   // 2.0×10⁻⁵ M
  std2: 0.050,   // 5.0×10⁻⁵ M
  std3: 0.100,   // 1.0×10⁻⁴ M
  std4: 0.500,   // 5.0×10⁻⁴ M
  std5: 0.750,   // 7.5×10⁻⁴ M
  unknown: 0.350 // cola sample (typical)
}

// Color gradient for standards (pale to deep yellow) based on concentration
const getStandardColor = (conc: number): string => {
  const intensity = conc / 0.00075 // normalize to max concentration
  const r = 255
  const g = 253 - Math.round(intensity * (253 - 230))
  const b = 231 - Math.round(intensity * (231 - 120))
  return `rgb(${r}, ${g}, ${b})`
}

const Part3Measurement = () => {
  const { state, updateExp3Part3, completeStep, update } = useLab()
  const { exp3Part3, currentStep, labMode, completedSteps } = state

  const [showInfo, setShowInfo] = useState<number | null>(null)
  const [currentCuvette, setCurrentCuvette] = useState<string | null>(null)
  const [displayedAbsorbance, setDisplayedAbsorbance] = useState(0.000)
  const [isInserting, setIsInserting] = useState(false)
  const [isReading, setIsReading] = useState(false)

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return
    const firstIncomplete = steps.find(s => !completedSteps.includes(`measurement-${s.id}`))
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s
  const isDone = (s: number) => completedSteps.includes(`measurement-${s}`)

  // Cuvette definitions (molar concentrations per config)
  const cuvettes = [
    { id: 'blank', label: 'Blank', conc: 0, step: 2 },
    { id: 'std1', label: '2.0×10⁻⁵ M', conc: 0.00002, step: 3 },
    { id: 'std2', label: '5.0×10⁻⁵ M', conc: 0.00005, step: 4 },
    { id: 'std3', label: '1.0×10⁻⁴ M', conc: 0.0001, step: 5 },
    { id: 'std4', label: '5.0×10⁻⁴ M', conc: 0.0005, step: 6 },
    { id: 'std5', label: '7.5×10⁻⁴ M', conc: 0.00075, step: 7 },
    { id: 'unknown', label: 'Cola (×3)', conc: 0.00035, step: 8, isUnknown: true }
  ]

  // Handlers
  const handleSetWavelength = () => {
    if (!isActive(1)) return
    updateExp3Part3({ wavelength: 400 })
    completeStep('measurement-1')
  }

  const handleInsertCuvette = (cuvetteId: string, stepNum: number) => {
    if (!isActive(stepNum)) return
    if (stepNum === 2 && !isDone(1)) return // Must set wavelength first
    if (stepNum > 2 && !exp3Part3.isZeroed) return // Must zero first

    setIsInserting(true)
    setTimeout(() => {
      setCurrentCuvette(cuvetteId)
      setIsInserting(false)
      updateExp3Part3({ currentSample: cuvetteId })

      // Simulate reading delay
      setIsReading(true)
      setTimeout(() => {
        const absorbance = EXPECTED_ABSORBANCES[cuvetteId] + (Math.random() - 0.5) * 0.01
        setDisplayedAbsorbance(absorbance)
        setIsReading(false)
      }, 800)
    }, 500)
  }

  const handleRecordReading = (cuvetteId: string, stepNum: number) => {
    if (isReading || currentCuvette !== cuvetteId) return

    const readings = { ...exp3Part3.readings, [cuvetteId]: displayedAbsorbance }
    updateExp3Part3({ readings })

    if (cuvetteId === 'blank') {
      // Zero the instrument
      updateExp3Part3({ isZeroed: true })
      setDisplayedAbsorbance(0.000)
    }

    completeStep(`measurement-${stepNum}`)
    setCurrentCuvette(null)
    updateExp3Part3({ currentSample: null })
  }

  const handleRemoveCuvette = () => {
    setCurrentCuvette(null)
    setDisplayedAbsorbance(0.000)
    updateExp3Part3({ currentSample: null })
  }

  const completedCount = completedSteps.filter((s: string) => s.startsWith('measurement')).length

  return (
    <div className="part3-measurement">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part III: Measurement</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(s.id) }}
                  >
                    ?
                  </button>
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Readings Summary */}
        <div className="readings-summary">
          <h4>Recorded Readings</h4>
          <div className="readings-list">
            {Object.entries(exp3Part3.readings).map(([id, absorbance]) => (
              <div key={id} className="reading-item">
                <span className="reading-label">{id === 'blank' ? 'Blank' : id === 'unknown' ? 'Cola' : id.replace('std', 'Std ')}</span>
                <span className="reading-value">{(absorbance as number).toFixed(3)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Lab Bench</h3>
          </div>
          <span className="bench-progress">{completedCount}/7 Complete</span>
        </div>

        <div className="bench-area">
          {/* Equipment Layout */}
          <div className="equipment-grid">
            {/* Spectrophotometer */}
            <div className="spectrophotometer-container">
              <div className="spec-display">
                <div className="spec-wavelength">
                  <span className="spec-label">Wavelength</span>
                  <span className="spec-value">{exp3Part3.wavelength} nm</span>
                  {!isDone(1) && (
                    <button
                      className="set-wavelength-btn"
                      onClick={handleSetWavelength}
                    >
                      Confirm 400 nm
                    </button>
                  )}
                </div>
                <div className="spec-absorbance">
                  <span className="spec-label">Absorbance</span>
                  <span className={`spec-value ${isReading ? 'reading' : ''}`}>
                    {isReading ? '-.---' : displayedAbsorbance.toFixed(3)}
                  </span>
                </div>
                <div className="spec-status">
                  {exp3Part3.isZeroed ? (
                    <span className="status-zeroed">Zeroed</span>
                  ) : (
                    <span className="status-not-zeroed">Not Zeroed</span>
                  )}
                </div>
              </div>

              {/* Sample Holder */}
              <div className="sample-holder">
                <div className="holder-label">Sample Holder</div>
                <div className="holder-slot">
                  {currentCuvette ? (
                    <div className="inserted-cuvette">
                      <Cuvette
                        fillLevel={75}
                        fillColor={
                          currentCuvette === 'unknown'
                            ? '#D4A574'
                            : getStandardColor(cuvettes.find(c => c.id === currentCuvette)?.conc || 0)
                        }
                        isInHolder={true}
                      />
                      <button
                        className="remove-btn"
                        onClick={handleRemoveCuvette}
                      >
                        <ArrowLeft size={14} />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="empty-slot">
                      <span>Insert Cuvette</span>
                      {isInserting && <div className="inserting-indicator" />}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cuvette Rack */}
            <div className="cuvette-rack">
              <div className="rack-label">Cuvette Rack</div>
              <div className="cuvettes-row">
                {cuvettes.map(cuvette => {
                  const canInsert = isActive(cuvette.step) && !isDone(cuvette.step) &&
                    (cuvette.step === 2 ? isDone(1) : exp3Part3.isZeroed)
                  const isInserted = currentCuvette === cuvette.id
                  const isRecorded = isDone(cuvette.step)

                  return (
                    <div key={cuvette.id} className="cuvette-slot">
                      {!isInserted && (
                        <>
                          <Cuvette
                            fillLevel={75}
                            fillColor={
                              cuvette.isUnknown
                                ? '#D4A574'
                                : getStandardColor(cuvette.conc)
                            }
                            label={cuvette.label}
                            active={canInsert}
                            onClick={canInsert ? () => handleInsertCuvette(cuvette.id, cuvette.step) : undefined}
                            disabled={!canInsert && !isRecorded}
                          />
                          {isRecorded && (
                            <div className="recorded-badge">
                              <Check size={12} />
                            </div>
                          )}
                        </>
                      )}
                      {isInserted && (
                        <div className="empty-cuvette-slot">
                          <span>In Use</span>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Record Button */}
            {currentCuvette && !isReading && (
              <button
                className="record-btn"
                onClick={() => {
                  const cuvette = cuvettes.find(c => c.id === currentCuvette)
                  if (cuvette) handleRecordReading(cuvette.id, cuvette.step)
                }}
              >
                <Check size={18} />
                {currentCuvette === 'blank' ? 'Zero & Record' : 'Record Reading'}
              </button>
            )}

            {/* Completion message */}
            {completedCount === 7 && (
              <div className="completion-message">
                <Check size={24} />
                <span>All Measurements Complete!</span>
                <p>Proceed to Part IV: Data Analysis</p>
              </div>
            )}
          </div>

          {/* Bench surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Info Box */}
      {showInfo && (
        <InfoBox
          step={showInfo}
          experimentId="exp3"
          stepInfo={getInfoContent(showInfo)}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part3-measurement {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 260px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 8px 10px;
          border-radius: 8px;
          margin-bottom: 2px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 11px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 10px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 10px;
          color: var(--text-secondary);
          margin-top: 2px;
          display: block;
        }

        .readings-summary {
          padding: 12px;
          border-top: 1px solid var(--border);
          background: var(--bg-elevated);
        }

        .readings-summary h4 {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-secondary);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .readings-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .reading-item {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          padding: 4px 8px;
          background: var(--bg-surface);
          border-radius: 4px;
        }

        .reading-label {
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        .reading-value {
          font-family: 'Courier New', monospace;
          color: var(--secondary);
          font-weight: 600;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .equipment-grid {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          margin-bottom: 70px;
        }

        .spectrophotometer-container {
          display: flex;
          gap: 20px;
          align-items: flex-start;
          background: var(--bg-surface);
          padding: 20px;
          border-radius: 12px;
          border: 2px solid var(--border);
        }

        .spec-display {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 150px;
        }

        .spec-wavelength,
        .spec-absorbance {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .spec-label {
          font-size: 10px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .spec-value {
          font-size: 24px;
          font-family: 'Courier New', monospace;
          font-weight: bold;
          color: #00FF00;
          background: #001a00;
          padding: 8px 12px;
          border-radius: 4px;
        }

        .spec-value.reading {
          animation: blink 0.5s ease-in-out infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .set-wavelength-btn {
          background: var(--primary);
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 4px;
        }

        .set-wavelength-btn:hover {
          background: var(--primary-dark);
        }

        .spec-status {
          padding-top: 8px;
        }

        .status-zeroed {
          color: var(--secondary);
          font-size: 12px;
          font-weight: 600;
        }

        .status-not-zeroed {
          color: var(--text-muted);
          font-size: 12px;
        }

        .sample-holder {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .holder-label {
          font-size: 11px;
          color: var(--text-muted);
          text-align: center;
        }

        .holder-slot {
          width: 80px;
          height: 100px;
          background: #1A1A1A;
          border: 2px solid #3A3A3A;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .inserted-cuvette {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .remove-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 10px;
          cursor: pointer;
        }

        .remove-btn:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        .empty-slot {
          color: var(--text-muted);
          font-size: 10px;
          text-align: center;
        }

        .inserting-indicator {
          width: 30px;
          height: 3px;
          background: var(--primary);
          border-radius: 2px;
          margin-top: 8px;
          animation: pulse 0.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }

        .cuvette-rack {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .rack-label {
          font-size: 12px;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .cuvettes-row {
          display: flex;
          gap: 16px;
          padding: 16px;
          background: var(--bg-elevated);
          border-radius: 8px;
          border: 1px solid var(--border);
        }

        .cuvette-slot {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .recorded-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          width: 18px;
          height: 18px;
          background: var(--secondary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .empty-cuvette-slot {
          width: 50px;
          height: 80px;
          border: 2px dashed var(--border);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
          font-size: 9px;
        }

        .record-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 24px;
          background: var(--secondary);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .record-btn:hover {
          background: var(--secondary-dark);
          transform: translateY(-2px);
        }

        .completion-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 24px;
          background: rgba(0, 168, 107, 0.15);
          border-radius: 12px;
          border: 2px solid var(--secondary);
        }

        .completion-message svg {
          color: var(--secondary);
        }

        .completion-message span {
          font-size: 16px;
          font-weight: 600;
          color: var(--secondary);
        }

        .completion-message p {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0;
        }

        .bench-surface {
          display: none;
        }
      `}</style>
    </div>
  )
}

// Info content for each step
function getInfoContent(step: number) {
  const content: Record<number, { title: string; content: string }> = {
    1: {
      title: 'Why 400 nm?',
      content: 'The yellow phosphate-vanadomolybdate complex absorbs blue light (around 400 nm) most strongly. We measure at the wavelength of maximum absorption for best sensitivity.'
    },
    2: {
      title: 'Zeroing the Spectrophotometer',
      content: 'The blank contains all reagents except phosphate. Zeroing with the blank sets A=0.000 and corrects for any absorbance from the solvent and reagents themselves.'
    },
    3: {
      title: 'Measuring Standards',
      content: 'Insert each standard cuvette carefully. Wait for the reading to stabilize before recording. Handle cuvettes by the frosted top to avoid fingerprints on the optical surfaces.'
    },
    4: {
      title: 'Beer-Lambert Law',
      content: 'Absorbance is directly proportional to concentration (A = abc), where A is absorbance, a is the extinction coefficient, b is the solution path length, and c is concentration. As concentration increases, absorbance increases linearly. This relationship enables the calibration curve.'
    },
    5: {
      title: 'Calibration Range',
      content: 'The standards establish points on the calibration curve. The unknown concentration must fall within this range for accurate determination.'
    },
    6: {
      title: 'Highest Standards',
      content: 'The 5.0×10⁻⁴ M and 7.5×10⁻⁴ M standards give the highest absorbance. If readings exceed ~0.8, dilute samples and multiply the result by the dilution factor.'
    },
    7: {
      title: 'Cola Sample Measurement',
      content: 'Add 5 mL of the ammonium vanadomolybdate solution to 10 mL of the dilute cola solution and measure the absorbance at 400 nm. If the absorbance is not within your calibration range, prepare either a more dilute or more concentrated solution. Measure the absorbance of three separate samples and report the average phosphoric acid concentration (remember to account for the dilution).'
    }
  }
  return content[step]
}

export default Part3Measurement
